import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const techGroups = [
  {
    category: "AI & Intelligence",
    items: [
      { name: "Neural Engines", icon: FiIcons.FiCpu, desc: "Custom LLM implementations" },
      { name: "Vector Databases", icon: FiIcons.FiDatabase, desc: "High-dimensional data retrieval" },
      { name: "LangChain", icon: FiIcons.FiLink, desc: "Complex agent orchestration" },
      { name: "OpenClaw / Hermes", icon: FiIcons.FiZap, desc: "Advanced reasoning & inference" }
    ]
  },
  {
    category: "Backend Engineering",
    items: [
      { name: "Node.js / Go", icon: FiIcons.FiServer, desc: "High-concurrency microservices" },
      { name: "Laravel / FastAPI", icon: FiIcons.FiHardDrive, desc: "High-performance API frameworks" },
      { name: "Rest API / MCPs", icon: FiIcons.FiShare2, desc: "Standardized & Model Context Protocols" },
      { name: "Docker / K8s", icon: FiIcons.FiBox, desc: "Orchestrated container systems" }
    ]
  },
  {
    category: "Frontend Engineering",
    items: [
      { name: "React / Next.js", icon: FiIcons.FiLayout, desc: "Enterprise-grade SSR frameworks" },
      { name: "Vue / Nuxt.js", icon: FiIcons.FiCommand, desc: "Modular frontend architectures" },
      { name: "Tailwind CSS", icon: FiIcons.FiGrid, desc: "Utility-first design systems" },
      { name: "Playwright", icon: FiIcons.FiCheckSquare, desc: "End-to-end testing & automation" }
    ]
  },
  {
    category: "Core Architecture",
    items: [
      { name: "Distributed Systems", icon: FiIcons.FiLayers, desc: "Scalable microservices" },
      { name: "Event-Driven", icon: FiIcons.FiZap, desc: "Real-time data pipelines" },
      { name: "Security", icon: FiIcons.FiShield, desc: "Zero-trust infrastructure" },
      { name: "Cloud Native", icon: FiIcons.FiCloud, desc: "AWS/GCP orchestration" }
    ]
  }
];

const TechStack = () => {
  return (
    <section className="py-32 bg-black border-t border-zinc-900 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
          <div className="max-w-2xl">
            <h2 className="text-sm font-mono text-cyan-500 mb-4 tracking-widest uppercase">Technology Primitives</h2>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tighter">
              The Engine Room of <br />
              <span className="text-zinc-500">Complex Systems.</span>
            </h3>
          </div>
          <p className="text-zinc-500 max-w-xs text-sm font-light leading-relaxed">
            We utilize a curated selection of industry-standard and cutting-edge technologies to ensure stability at scale.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-12">
          {techGroups.map((group, groupIdx) => (
            <div key={groupIdx} className="space-y-6">
              <h4 className="text-zinc-400 font-medium text-lg px-2 border-l-2 border-cyan-500/20">{group.category}</h4>
              <div className="grid grid-cols-1 gap-4">
                {group.items.map((item, itemIdx) => (
                  <motion.div
                    key={itemIdx}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: (groupIdx * 4 + itemIdx) * 0.03 }}
                    className="group p-6 rounded-2xl border border-zinc-900 bg-zinc-950/50 hover:border-cyan-500/30 transition-all duration-500"
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-zinc-900 text-cyan-400 group-hover:bg-cyan-500/10 transition-colors">
                        <SafeIcon icon={item.icon} className="text-xl" />
                      </div>
                      <div>
                        <div className="text-white font-medium mb-1">{item.name}</div>
                        <div className="text-zinc-500 text-[10px] uppercase tracking-wider font-mono">{item.desc}</div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Background decoration */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />
    </section>
  );
};

export default TechStack;